/**
* Jsll.ts
* @author Ram Thiru (ramthi) and Hector Hernandez (hectorh)
* @copyright Microsoft 2020
* JSLL representation.
*/
import { ActionType, Behavior, DataCollector } from "@microsoft/1ds-wa-js";
import { isArray, isValueAssigned, objForEachKey } from "@microsoft/applicationinsights-core-js";
import { objExtend } from "@nevware21/ts-utils";
import { ApplicationInsights as JsllSKU } from "./ApplicationInsights";
import { ClickstreamTracker } from "./ClickstreamTracker";
import { Cookie } from "./Cookie";
import { Cv } from "./Cv";
import { ErrorHandler } from "./ErrorHandler";
import { Ids } from "./Ids";
import { Ix } from "./Ix";
import { Logger } from "./Logger";
import { Service } from "./Service";
import { TimespanHandler } from "./TimespanHandler";
import { Utils, notImplementedWarn } from "./Utils";
import { VortexEvents } from "./VortexEvents";
var JavascriptLoggingLibrary = /** @class */ (function () {
    function JavascriptLoggingLibrary() {
        var _this = this;
        this.isAvailable = true;
        this.requestBody = "";
        this._schemas = [];
        this.testHook = {};
        // tslint:disable-next-line: no-empty
        this.OnSuccessfulVortexRequest = function () { };
        this.sku = new JsllSKU();
        this.ct = new ClickstreamTracker(this.sku);
        this.cv = new Cv(this.sku);
        this.ids = new Ids(this.sku);
        this.ix = new Ix();
        this.logger = new Logger(this.sku);
        this.timespanHandler = new TimespanHandler();
        this.service = new Service();
        this.utils = new Utils();
        this.cookie = new Cookie();
        this.errorHandler = new ErrorHandler();
        this.vortexEvents = new VortexEvents();
        this.behavior = Behavior;
        this.behaviorKeys = [];
        objForEachKey(Behavior, function (behaviorKey) {
            _this.behaviorKeys.push(behaviorKey);
        });
        this.actionType = ActionType;
    }
    JavascriptLoggingLibrary.prototype.init = function (options) {
        // Default value is different for 1DS
        if (options.useDefaultContentName == undefined) {
            options.useDefaultContentName = true;
        }
        if (options.useShortNameForContentBlob == undefined) {
            options.useShortNameForContentBlob = false;
        }
        this._awaConfig = options;
        var config = objExtend({}, options.advancedConfig);
        config.instrumentationKey = this._awaConfig.instrumentationKey;
        config.jsllConfiguration = this._awaConfig;
        this.sku.initialize(config, []);
        this.isInitialized = this.sku.isInitialized();
    };
    JavascriptLoggingLibrary.prototype.writeEvent = function (eventData, doNotRetryOnError) {
        var jsllData = eventData;
        if (isArray(jsllData)) {
            for (var i = 0; i < jsllData.length; i++) {
                this.sku.track(this.transformCustomEvent(jsllData[i]));
            }
        }
        else {
            this.sku.track(this.transformCustomEvent(jsllData));
        }
    };
    JavascriptLoggingLibrary.prototype.getConfig = function () {
        return this._awaConfig;
    };
    JavascriptLoggingLibrary.prototype.extendCoreData = function (coreData) {
        this.sku.getWebAnalyticsExtension().updateCoreDataConfig(coreData);
    };
    /**
     * Unload and Tear down the SDK and any initialized plugins, after calling this the SDK will be considered
     * to be un-initialized and non-operational, re-initializing the SDK should only be attempted if the previous
     * unload call return `true` stating that all plugins reported that they also unloaded, the recommended
     * approach is to create a new instance and initialize that instance.
     * This is due to possible unexpected side effects caused by plugins not supporting unload / teardown, unable
     * to successfully remove any global references or they may just be completing the unload process asynchronously.
     * If you pass isAsync as `true` (also the default) and DO NOT pass a callback function then an [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * will be returned which will resolve once the unload is complete. The actual implementation of the `IPromise`
     * will be a native Promise (if supported) or the default as supplied by [ts-async library](https://github.com/nevware21/ts-async)
     * @param isAsync - Can the unload be performed asynchronously (default)
     * @param unloadComplete - An optional callback that will be called once the unload has completed
     * @param cbTimeout - An optional timeout to wait for any flush operations to complete before proceeding with the
     * unload. Defaults to 5 seconds.
     * @return Nothing or if occurring asynchronously a [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * which will be resolved once the unload is complete, the [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * will only be returned when no callback is provided and isAsync is true
     */
    JavascriptLoggingLibrary.prototype.unload = function (isAsync, unloadComplete, cbTimeout) {
        return this.sku.unload(isAsync, unloadComplete, cbTimeout);
    };
    JavascriptLoggingLibrary.prototype.capabilitiesCheck = function () {
        if (!JSON || !JSON.stringify) {
            this.logger.logError("Unable to write event: the global JSON.stringify method does not exist");
            this.isAvailable = false;
        }
    };
    JavascriptLoggingLibrary.prototype.getSignedInStatus = function () {
        return DataCollector._getSignedInStatus(this.sku.getWebAnalyticsExtensionConfig());
    };
    JavascriptLoggingLibrary.prototype._validateAndTranslateEvent = function (eventObject) {
        return { event: eventObject };
    };
    JavascriptLoggingLibrary.prototype.sanitizeUrl = function (originalUrl) {
        var location = document.createElement("a");
        location.href = originalUrl;
        // Adding anchor to DOM prevents IE issue that generates wrong pathname
        // https://stackoverflow.com/questions/956233/javascript-pathname-ie-quirk
        document.body.appendChild(location);
        document.body.removeChild(location);
        if (location && originalUrl) {
            return DataCollector._sanitizeUrl(this.sku.getWebAnalyticsExtensionConfig(), location);
        }
        return "";
    };
    JavascriptLoggingLibrary.prototype.sanitizeCurrentUrl = function () {
        // check for window.location because in IE8 anyone can replace the location object
        if (window.location) {
            return DataCollector._sanitizeUrl(this.sku.getWebAnalyticsExtensionConfig(), window.location);
        }
        return "";
    };
    JavascriptLoggingLibrary.prototype.getQueryStringParameters = function () {
        // Query string parameter is not relevant here, use case in JSLL is most likely related to debugging
        // NO OP
        notImplementedWarn("getQueryStringParameters");
        return "";
    };
    JavascriptLoggingLibrary.prototype._registerSchemas = function (schemas) {
        // Schemas don't need to be registered to send data from 1DS
        // NO OP
        notImplementedWarn("_registerSchemas");
    };
    JavascriptLoggingLibrary.prototype.sendEventThroughIframe = function (frameSrc) {
        // Event was exposed for communication with Vortex for auth scenarios, communication is taken care of in auth extension now
        // NO OP
        notImplementedWarn("sendEventThroughIframe");
    };
    JavascriptLoggingLibrary.prototype.isEventValid = function (event) {
        // No need to validate format in 1DS, all events would be valid
        // NO OP
        notImplementedWarn("isEventValid");
        return true;
    };
    JavascriptLoggingLibrary.prototype.translateEventFromIntermediateStructure = function (oldEventFormat) {
        // NO OP
        notImplementedWarn("translateEventFromIntermediateStructure");
    };
    JavascriptLoggingLibrary.prototype.firstEventDone = function () {
        // First event is irrelevant in 1DS, all events will be send using same logic
        // NO OP
        notImplementedWarn("firstEventDone");
    };
    JavascriptLoggingLibrary.prototype.firstEventDoneTasks = function () {
        // First event is irrelevant in 1DS, all events will be send using same logic
        // NO OP
        notImplementedWarn("firstEventDoneTasks");
    };
    JavascriptLoggingLibrary.prototype.resetFirstEvent = function () {
        // First event is irrelevant in 1DS, all events will be send using same logic
        // NO OP
        notImplementedWarn("resetFirstEvent");
    };
    /**
     * Transform JSLL custom event to 1DS
     */
    JavascriptLoggingLibrary.prototype.transformCustomEvent = function (eventData) {
        var event = {
            name: eventData.name,
            baseType: eventData.data && eventData.data.baseType ? eventData.data.baseType : ""
        };
        // Add part B
        event.baseData = event.baseData || {};
        if (eventData.data && eventData.data.baseData) {
            objForEachKey(eventData.data.baseData, function (key, value) {
                event.baseData[key] = value;
            });
        }
        // Add part C
        event.data = event.data || {};
        if (eventData.data) {
            objForEachKey(eventData.data, function (key, value) {
                if (key !== "baseData") {
                    event.data[key] = eventData.data[key];
                }
            });
        }
        if (isValueAssigned(eventData.serviceName)) {
            event.ext = event.ext || {};
            event.ext["intweb"] = event.ext["intweb"] || {};
            event.ext["intweb"]["serviceName"] = eventData.serviceName;
        }
        if (isValueAssigned(eventData.cV)) {
            event.ext = event.ext || {};
            event.ext["mscv"] = event.ext["mscv"] || {};
            event.ext["mscv"]["cV"] = eventData.cV;
        }
        if (isValueAssigned(eventData.appId)) {
            event.ext = event.ext || {};
            event.ext["app"] = event.ext["app"] || {};
            event.ext["app"]["id"] = eventData.appId;
        }
        if (isValueAssigned(eventData.env)) {
            event.ext = event.ext || {};
            event.ext["app"] = event.ext["app"] || {};
            event.ext["app"]["env"] = eventData.env;
        }
        // TODO: isSensitive flag is not supported in 1DS Web SDK
        return event;
    };
    return JavascriptLoggingLibrary;
}());
export { JavascriptLoggingLibrary };
//# sourceMappingURL=Jsll.js.map